import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';

const discoverIcons: Record<string, string> = {
  'genie-examples': '/icons/discover/icon-genie-examples.png',
  'blog': '/icons/discover/icon-blog.png',
  'guides': '/icons/discover/icon-guides.png',
  'comparisons': '/icons/discover/icon-comparisons.png',
  'use-cases': '/icons/discover/icon-use-cases.png',
  'case-studies': '/icons/discover/icon-case-studies.png',
  'insights': '/icons/discover/icon-insights.png',
  'updates': '/icons/discover/icon-updates.png',
  'integrations': '/icons/discover/icon-integrations.png',
  'experience': '/icons/discover/icon-genie-examples.png',
};

const resourceItems = [
  { id: 'blog', label: 'Blog', href: '/blog', description: 'Latest articles and how-tos' },
  { id: 'guides', label: 'Guides', href: '/guides', description: 'Step-by-step walkthroughs' },
  { id: 'comparisons', label: 'Comparisons', href: '/compare', description: 'See how options stack up' },
  { id: 'use-cases', label: 'Use Cases', href: '/use-cases', description: 'Real-world applications' },
  { id: 'case-studies', label: 'Case Studies', href: '/case-studies', description: 'Customer success stories' },
  { id: 'insights', label: 'Insights', href: '/insights', description: 'Industry trends and data' },
  { id: 'updates', label: 'Updates', href: '/updates', description: 'Product news and releases' },
  { id: 'integrations', label: 'Integrations', href: '/integrations', description: 'Connect your tools' },
];

const DiscoverDropdown = () => {
  return (
    <NavigationMenu className="[&>div]:right-0 [&>div]:left-auto">
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent text-white text-base font-medium hover:bg-white/10 hover:text-white focus:bg-white/10 data-[state=open]:bg-white/10 px-2 py-2 h-auto">
            Discover
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-[min(720px,calc(100vw-2rem))] bg-white/95 backdrop-blur-xl border border-gray-200 rounded-xl p-4 md:p-6 shadow-2xl">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* Explore Genies - Left Column (top on narrow screens) */}
                <div className="md:w-[180px] lg:w-[200px] shrink-0 md:border-r border-b md:border-b-0 border-gray-200 pb-4 md:pb-0 md:pr-6">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 md:mb-4">
                    Explore
                  </h3>
                  <NavigationMenuLink asChild>
                    <a
                      href="/discover"
                      className={cn(
                        "group flex md:flex-col gap-3 md:gap-2 p-3 rounded-xl transition-all duration-200 items-center md:items-start",
                        "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                      )}
                    >
                      <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shrink-0">
                        <img src={discoverIcons['genie-examples']} alt="Genie Examples" className="w-6 h-6 object-contain" />
                      </div>
                      <div className="space-y-0.5">
                        <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                          Genie Examples
                        </h4>
                        <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                          Browse and try live voice genies across industries.
                        </p>
                      </div>
                    </a>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <a
                      href="/explore"
                      className={cn(
                        "group flex md:flex-col gap-3 md:gap-2 p-3 rounded-xl transition-all duration-200 items-center md:items-start",
                        "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                      )}
                    >
                      <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shrink-0">
                        <img src={discoverIcons['experience']} alt="Experience" className="w-6 h-6 object-contain" />
                      </div>
                      <div className="space-y-0.5">
                        <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                          Experience
                        </h4>
                        <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                          See Help Genie in action across industries.
                        </p>
                      </div>
                    </a>
                  </NavigationMenuLink>
                </div>

                {/* Resources - Right Column */}
                <div className="flex-1">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 md:mb-4">
                    Resources
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {resourceItems.map((item) => (
                      <NavigationMenuLink key={item.href} asChild>
                        <a
                          href={item.href}
                          className={cn(
                            "group flex items-start gap-3 p-3 rounded-xl transition-all duration-200",
                            "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                          )}
                        >
                          <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg shrink-0 mt-0.5">
                            <img src={discoverIcons[item.id]} alt={item.label} className="w-4 h-4 object-contain" />
                          </div>
                          <div className="space-y-0.5">
                            <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                              {item.label}
                            </h4>
                            <p className="text-gray-500 text-xs leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </a>
                      </NavigationMenuLink>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default DiscoverDropdown;
